//internal-deps
import {
    ADD_MONEY_ELIGIBLE_WALLETS,
    PHONEPE_PLACE_ORDER_TYPE_KEY,
    PHONEPE_PLACE_ORDER_TYPE,
    REDIRECT_SUPPORTED_WALLETS,
    VALID_WALLET_LINK_STATUSES,
    VALID_WALLET_TYPES,
    WALLET_CODES,
} from "@swiggy-private/payment-core";
import { format, isEmpty } from "@swiggy-private/core-ui";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";

//helpers
import {
    getPaymentAriaLabel,
    getPaymentDisableAriaLabel,
    getPaymentLinkedAriaLabel,
    getPaymentMethodDisableAriaLabel,
} from "@payment-ui/helpers/Accessibility";

//defs
import { LinkedWalletDetails, PaymentMethod, PaymentMethodMeta } from "@payment-ui/interfaces";
import { WalletData, WalletPaymentGroupActions, WalletPaymentMethodData } from "@payment-ui/interfaces/Wallet";

//constants
import { WALLET_DEEP_INTEGRATION_SUFFIX } from "@payment-ui/constants/Wallet";
import { TEXTS } from "@payment-ui/constants/index";

export const isAmazonWallet = (methodData: WalletPaymentMethodData): boolean => {
    return (
        methodData.paymentCode.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAY ||
        methodData.paymentCode.toUpperCase() === VALID_WALLET_TYPES.AMAZONPAYWEB
    );
};

export const isAddMoneyEligible = (methodData: WalletPaymentMethodData): boolean =>
    ADD_MONEY_ELIGIBLE_WALLETS.indexOf(methodData.name.toUpperCase() as VALID_WALLET_TYPES) > -1;

export const isCheckBalanceInRetryMode = (status: VALID_WALLET_LINK_STATUSES): boolean =>
    status === VALID_WALLET_LINK_STATUSES.RETRY;

export const isWalletLinked = (status: VALID_WALLET_LINK_STATUSES): boolean =>
    status === VALID_WALLET_LINK_STATUSES.LINKED;

export const isWalletTokenExpired = (status: VALID_WALLET_LINK_STATUSES): boolean =>
    status === VALID_WALLET_LINK_STATUSES.TOKEN_EXPIRED;

export const isWalletLinkedMobileNoDiffrentFromUserMobileNo = (
    walletLinkedNumber: string,
    userMobileNumber: string,
): boolean => walletLinkedNumber !== userMobileNumber;

export const getPaymentMethodCode = (methodData: WalletPaymentMethodData): string => {
    return methodData.paymentCode.replace(WALLET_DEEP_INTEGRATION_SUFFIX, "");
};

export const getAccessibleMobileText = (mobile: string): string => {
    return mobile.split("").join(" ");
};
/**
 * Return weather wallet support redirect flow
 * @param methodData : wallet method data
 * @returns isRedirectSupported: boolean
 */
export const isRedirectFlowSupported = (methodData: WalletPaymentMethodData): boolean => {
    const paymentCode = getPaymentMethodCode(methodData);
    return REDIRECT_SUPPORTED_WALLETS.indexOf(paymentCode.toUpperCase() as VALID_WALLET_TYPES) > -1;
};

/**
 * Check if the method is Phonepe wallet
 * @param methodData : wallet method data
 * @returns boolean
 */
export const isPhonePePaymentMethod = (methodData: WalletPaymentMethodData): boolean => {
    const paymentCode = getPaymentMethodCode(methodData);
    return paymentCode.toUpperCase() === VALID_WALLET_TYPES.PHONEPE;
};

/**
 * Return extra params required for payment in case of Phonepe Wallet
 * @param isPhonepeSdkAvailable : is Phonepe SDK available
 * @returns PhoneExtraParams
 */
export const getPhonePeParams = (isPhonepeSdkAvailable = false): { [key: string]: string } => {
    return {
        [PHONEPE_PLACE_ORDER_TYPE_KEY]: isPhonepeSdkAvailable
            ? PHONEPE_PLACE_ORDER_TYPE.SDK
            : PHONEPE_PLACE_ORDER_TYPE.REDIRECT,
    };
};

/**
 * Return extra params required for payment in case of Amazon pay Sdk Wallet
 * @param isBalanceSufficient : is wallet has sufficent balance to make payment
 * @param paymentMetaId : payment meta id
 * @returns PhoneExtraParams
 */
export const getAmazonPaySdkParams = (
    isBalanceSufficient: boolean,
    paymentMetaId?: string,
    // eslint-disable-next-line @typescript-eslint/naming-convention
): { payment_method_meta: { isEnoughBalanceInWallet: string; amazon_payment_meta_data?: string } } => {
    return {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_method_meta: {
            isEnoughBalanceInWallet: `${isBalanceSufficient}`,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            amazon_payment_meta_data: paymentMetaId ?? "",
        },
    };
};

export const getWalletAriaLabel = ({
    paymentMethodData,
    walletData,
    isSelected,
    isEnabled,
}: {
    paymentMethodData: WalletPaymentMethodData;
    walletData: WalletData;
    isSelected: boolean;
    isEnabled: boolean;
}): string => {
    if (walletData.paymentMethodDisabled === true) {
        return getPaymentMethodDisableAriaLabel(paymentMethodData);
    }
    if (!isEnabled) {
        return getPaymentDisableAriaLabel(paymentMethodData);
    }
    if (!walletData.linked) {
        return getPaymentLinkedAriaLabel(paymentMethodData);
    }
    const showLessBalanceText = !walletData.isWalletBalanceSufficent && !isAmazonWallet(paymentMethodData);
    return getPaymentAriaLabel({
        paymentMethodData,
        paymentData: walletData,
        balance: walletData.balance,
        showLessBalanceText,
        isSelected,
    });
};

export const isValidPhoneNumber = (mobileNumber: string): boolean => {
    return !isEmpty(mobileNumber) && /^\d{10}$/.test(mobileNumber);
};

export const getAmountWithoutRupeeSymbol = (amount: string): number => {
    if (!amount.startsWith(TEXTS.RUPEE)) {
        return Number(amount);
    }
    return Number(amount.slice(1));
};

export const getAmountWithRupeeSymbol = (amount: number): string => TEXTS.RUPEE + amount;

export const getDelinkWalletSuccessTitle = (walletDetails: LinkedWalletDetails): string => {
    switch (walletDetails.walletCode) {
        case WALLET_CODES.amazonpay:
            return TEXTS.AMAZON_PAY_DELINK_SUCCESS_TITLE;
        case WALLET_CODES.simpl:
            return TEXTS.SIMPL_DELINK_SUCCESS_TITLE;
        case WALLET_CODES.paytmpostpaid:
        case WALLET_CODES.paytm:
            return format(TEXTS.DELINK_SUCCESS_ACCOUNT_TITLE, TEXTS.PAYTM, String(walletDetails.mobileNumber));
        case WALLET_CODES.mmt:
            return format(
                TEXTS.DELINK_SUCCESS_ACCOUNT_TITLE,
                walletDetails.displayName,
                walletDetails.corporateWalletDetails?.corporateEmail || "",
            );
        default:
            return format(TEXTS.DELINK_SUCCESS_TITLE, walletDetails.displayName, String(walletDetails.mobileNumber));
    }
};

export const getDelinkWalletSuccessSubTitle = (walletDetails: LinkedWalletDetails): string => {
    switch (walletDetails.walletCode) {
        case WALLET_CODES.simpl:
        case WALLET_CODES.mmt:
            return "";
        default:
            return TEXTS.DELINK_SUCCESS_SUBTEXT;
    }
};

export const getDelinkWalletFailureTitle = (walletDetails: LinkedWalletDetails): string => {
    if (walletDetails.walletCode === WALLET_CODES.amazonpay) {
        return TEXTS.AMAZON_PAY_DELINK_FAILURE_TITLE;
    }

    if (walletDetails.walletCode === WALLET_CODES.simpl) {
        return TEXTS.SIMPL_DELINK_FAILURE_TITLE;
    }

    if (walletDetails.walletCode === WALLET_CODES.mmt) {
        return format(
            TEXTS.DELINK_FAILURE_TITLE,
            walletDetails.displayName,
            walletDetails.corporateWalletDetails?.corporateEmail || "",
        );
    }

    return format(TEXTS.DELINK_FAILURE_TITLE, walletDetails.displayName, String(walletDetails.mobileNumber));
};

export const getDelinkWalletAlertTitle = (walletDetails: LinkedWalletDetails): string => {
    switch (walletDetails.walletCode) {
        case WALLET_CODES.paytmpostpaid:
        case WALLET_CODES.paytm:
            return format(TEXTS.DELINK_CONFIRMATION_METHOD_ACCOUNT_TITLE, TEXTS.PAYTM);
        case WALLET_CODES.simpl:
            return format(TEXTS.DELINK_CONFIRMATION_METHOD_ACCOUNT_TITLE, TEXTS.SIMPL);
        default:
            return TEXTS.DELINK_CONFIRMATION_TITLE;
    }
};

export const getDelinkWalletAlertSubTitle = (walletDetails: LinkedWalletDetails): string => {
    switch (walletDetails.walletCode) {
        case WALLET_CODES.paytmpostpaid:
        case WALLET_CODES.paytm:
            return format(TEXTS.DELINK_CONFIRMATION_METHOD_ACCOUNT_SUBTITLE, TEXTS.PAYTM);
        default:
            return "";
    }
};

export const getDelinkWalletCTA = (walletDetails: LinkedWalletDetails): string => {
    switch (walletDetails.walletCode) {
        case WALLET_CODES.paytmpostpaid:
        case WALLET_CODES.paytm:
            return format(TEXTS.DELINK_METHOD_ACCOUNT, TEXTS.PAYTM);
        case WALLET_CODES.simpl:
            return format(TEXTS.DELINK_METHOD_ACCOUNT, TEXTS.SIMPL);
        default:
            return TEXTS.DELINK;
    }
};

export const isWalletPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is WalletPaymentMethodData => {
    return (
        [VALID_PAYMENT_GROUP_TYPES.WALLET, VALID_PAYMENT_GROUP_TYPES.WALLET_WEB].indexOf(
            method.groupName.toUpperCase(),
        ) !== -1
    );
};

export const getWalletPaymentMethodExtraParams = (
    methodData: WalletPaymentMethodData,
    walletSpecificActions: WalletPaymentGroupActions,
) => {
    const _isAmazonWallet = isAmazonWallet(methodData);
    const { isPhonepeSdkAvailable } = walletSpecificActions.getPhonepeSdkStatus();
    let paymentMethodExtraParams;

    /** Add extra params for phonpe wallet for REDIRECT */
    if (isPhonePePaymentMethod(methodData)) {
        paymentMethodExtraParams = getPhonePeParams(isPhonepeSdkAvailable);
    }

    const { state: amazonPaySdkStatus } = walletSpecificActions.getAmazonPaySdkStatus();
    if (_isAmazonWallet && amazonPaySdkStatus) {
        paymentMethodExtraParams = getAmazonPaySdkParams(amazonPaySdkStatus.isBalanceSufficient);
    }
    return paymentMethodExtraParams;
};
